import { Observable, map, mapTo } from 'rxjs';
import getUnixTime from 'date-fns/getUnixTime';
import fromUnixTime from 'date-fns/fromUnixTime';
import { ADMIN_API } from '../AdminAPI';
import { convertUnixToDate, createBranch, Branch } from '@golden/gdk-base';
import { withdrawStatusMapAdminWithdrawStatus } from '../constants/withdrawStatusMapAdminWithdrawStatus';
import { withdrawDetailStatusMapAdminWithdrawStatus } from '../constants/withdrawDetailStatusMapAdminWithdrawStatus';
export function createWithdrawService(api, trunk) {
    let withdrawSlipId = null;
    const getWithdrawSlipAccounting = (id) => {
        if (!id) {
            return new Observable().pipe(mapTo({
                is_first_withdraw: false,
                cash_last_balance: null,
                cash_accounting: null,
                cash_self_balance: null,
                difference: null,
                start_at: new Date(),
                end_at: new Date(),
                last_balances: [],
                self_balances: []
            }));
        }
        return api.fetch(ADMIN_API.GET_WITHDRAW_SLIP_ACCOUNTING, { config: { urlParams: { id } } }).pipe(map((res) => ({
            ...convertUnixToDate(res, 'start_at', 'end_at'),
            last_balances: res.last_balances.map((data) => convertUnixToDate(data, 'fetched_at')),
            self_balances: res.self_balances.map((data) => convertUnixToDate(data, 'fetched_at'))
        })));
    };
    return {
        get withdrawSlipId() {
            return withdrawSlipId;
        },
        set withdrawSlipId(id) {
            withdrawSlipId = id;
            if (id)
                trunk.next([Branch.ADMIN_WITHDRAW_SLIP_ACCOUNTING]);
        },
        withdrawSlipAccounting: createBranch({
            trunk,
            branch: Branch.ADMIN_WITHDRAW_SLIP_ACCOUNTING,
            value: {
                is_first_withdraw: false,
                cash_last_balance: null,
                cash_accounting: null,
                cash_self_balance: null,
                difference: null,
                start_at: new Date(),
                end_at: new Date(),
                last_balances: [],
                self_balances: []
            },
            operator: () => getWithdrawSlipAccounting(withdrawSlipId)
        }),
        getCourierList() {
            return api.fetch(ADMIN_API.GET_WITHDRAW_COURIER_LIST);
        },
        getWithdrawSlipList(payload) {
            const data = { ...payload };
            if (data.created_at_start)
                data.created_at_start = getUnixTime(data.created_at_start);
            if (data.created_at_end)
                data.created_at_end = getUnixTime(data.created_at_end);
            if (data.finished_at_start)
                data.finished_at_start = getUnixTime(data.finished_at_start);
            if (data.finished_at_end)
                data.finished_at_end = getUnixTime(data.finished_at_end);
            return api.fetch(ADMIN_API.GET_WITHDRAW_SLIP_LIST, { data }).pipe(map((res) => ({
                ...res,
                data: res.data.map((item) => {
                    const newItem = { ...convertUnixToDate(item, 'created_at'), status: withdrawStatusMapAdminWithdrawStatus[item.status] };
                    if (item.finished_at)
                        newItem.finished_at = fromUnixTime(item.finished_at);
                    if (item.locked_at)
                        newItem.locked_at = fromUnixTime(item.locked_at);
                    return newItem;
                })
            })));
        },
        reviewWithdrawSlip(id) {
            return api.fetch(ADMIN_API.POST_WITHDRAW_SLIP_REVIEW, { config: { urlParams: { id } } });
        },
        unlockWithdrawSlip(id) {
            return api.fetch(ADMIN_API.POST_WITHDRAW_SLIP_UNLOCK, { config: { urlParams: { id } } });
        },
        stopThirdPartyWithdraw(id) {
            return api.fetch(ADMIN_API.POST_WITHDRAW_SLIP_STOP_THIRD_PARTY, { config: { urlParams: { id } } });
        },
        getWithdrawSlipDetail(id) {
            return api.fetch(ADMIN_API.GET_WITHDRAW_SLIP_DETAIL, { config: { urlParams: { id } } }).pipe(map((res) => ({
                ...convertUnixToDate(res, 'register_at', 'created_at', 'finished_at'),
                status: withdrawStatusMapAdminWithdrawStatus[res.status],
                details: res.details?.map((item) => ({
                    ...convertUnixToDate(item, 'finished_at'),
                    status: withdrawDetailStatusMapAdminWithdrawStatus[item.status]
                }))
            })));
        },
        getWithdrawRCSlipList(payload) {
            const data = { ...payload };
            if (data.created_at_start)
                data.created_at_start = getUnixTime(data.created_at_start);
            if (data.created_at_end)
                data.created_at_end = getUnixTime(data.created_at_end);
            if (data.finished_at_start)
                data.finished_at_start = getUnixTime(data.finished_at_start);
            if (data.finished_at_end)
                data.finished_at_end = getUnixTime(data.finished_at_end);
            if (data.rc_apply_at_start)
                data.rc_apply_at_start = getUnixTime(data.rc_apply_at_start);
            if (data.rc_apply_at_end)
                data.rc_apply_at_end = getUnixTime(data.rc_apply_at_end);
            return api.fetch(ADMIN_API.GET_WITHDRAW_SLIP_RC_LIST, { data }).pipe(map((res) => ({
                ...res,
                data: res.data.map((item) => {
                    const newItem = { ...convertUnixToDate(item, 'created_at') };
                    if (item.finished_at)
                        newItem.finished_at = fromUnixTime(item.finished_at);
                    if (item.locked_at)
                        newItem.locked_at = fromUnixTime(item.locked_at);
                    if (item.rc_apply_at)
                        newItem.rc_apply_at = fromUnixTime(item.rc_apply_at);
                    return newItem;
                })
            })));
        },
        reviewWithdrawRCSlip(id) {
            return api.fetch(ADMIN_API.POST_WITHDRAW_SLIP_RC_REVIEW, { config: { urlParams: { id } } });
        },
        unlockWithdrawRCSlip(id) {
            return api.fetch(ADMIN_API.POST_WITHDRAW_SLIP_RC_UNLOCK, { config: { urlParams: { id } } });
        },
        applyWithdrawRCSlip(id, data) {
            return api.fetch(ADMIN_API.POST_WITHDRAW_SLIP_RC_APPLY, { data, config: { urlParams: { id } } });
        },
        getWithdrawRCSlipDetail(id) {
            return api.fetch(ADMIN_API.GET_WITHDRAW_SLIP_RC_DETAIL, { config: { urlParams: { id } } }).pipe(map((res) => ({
                ...convertUnixToDate(res, 'register_at'),
                duplicate_bank_accounts: res.duplicate_bank_accounts.map((item) => convertUnixToDate(item, 'created_at', 'deleted_at'))
            })));
        },
        exportWithdrawRCSlipHistory(payload) {
            const data = { ...payload };
            if (data.created_at_start)
                data.created_at_start = getUnixTime(data.created_at_start);
            if (data.created_at_end)
                data.created_at_end = getUnixTime(data.created_at_end);
            if (data.finished_at_start)
                data.finished_at_start = getUnixTime(data.finished_at_start);
            if (data.finished_at_end)
                data.finished_at_end = getUnixTime(data.finished_at_end);
            if (data.rc_apply_at_start)
                data.rc_apply_at_start = getUnixTime(data.rc_apply_at_start);
            if (data.rc_apply_at_end)
                data.rc_apply_at_end = getUnixTime(data.rc_apply_at_end);
            return api.fetch(ADMIN_API.GET_EXPORT_WITHDRAW_RC_VALIDATE, { data });
        },
        getWithdrawSlipTransfer(id) {
            return api.fetch(ADMIN_API.GET_WITHDRAW_SLIP_TRANSFER, { config: { urlParams: { id } } }).pipe(map((res) => res.map((data) => convertUnixToDate(data, 'created_at', 'finished_at'))));
        },
        getWithdrawSlipExchangeRate(id) {
            return api.fetch(ADMIN_API.GET_WITHDRAW_SLIP_EXCHANGE_RATE, { config: { urlParams: { id } } }).pipe(map((res) => res.expired_at ? convertUnixToDate(res, 'expired_at') : { sell: res.sell, expired_at: null }));
        },
        goldenWithdrawSlip(id, data) {
            return api.fetch(ADMIN_API.POST_WITHDRAW_SLIP_TO_GOLDEN, { data, config: { urlParams: { id } } });
        },
        thirdPartyWithdrawSlip(id, data) {
            return api.fetch(ADMIN_API.POST_WITHDRAW_SLIP_TO_THIRD_PARTY, { data, config: { urlParams: { id } } });
        },
        cancelWithdrawSlip(id, data) {
            return api.fetch(ADMIN_API.POST_WITHDRAW_SLIP_CANCEL, { data, config: { urlParams: { id } } });
        },
        getWithdrawSlipCouriers(id) {
            return api.fetch(ADMIN_API.GET_WITHDRAW_SLIP_COURIER, { config: { urlParams: { id } } });
        },
        getAutoWithdraw() {
            return api.fetch(ADMIN_API.GET_AUTO_WITHDRAW);
        },
        putAutoWithdraw(data) {
            return api.fetch(ADMIN_API.PUT_AUTO_WITHDRAW, { data });
        },
        getWithdrawLimitList(data) {
            return api.fetch(ADMIN_API.GET_WITHDRAW_LIMIT_LIST, { data }).pipe(map((res) => ({
                ...res,
                data: res.data.map((item) => convertUnixToDate(item, 'updated_at'))
            })));
        },
        deleteWithdrawLimit(id) {
            return api.fetch(ADMIN_API.DELETE_WITHDRAW_LIMIT, { config: { urlParams: { id } } });
        },
        getWithdrawLimit(id) {
            return api.fetch(ADMIN_API.GET_WITHDRAW_LIMIT, { config: { urlParams: { id } } });
        },
        createWithdrawLimit(data) {
            return api.fetch(ADMIN_API.POST_WITHDRAW_LIMIT, { data });
        },
        updateWithdrawLimit(id, data) {
            return api.fetch(ADMIN_API.PUT_WITHDRAW_LIMIT, { data, config: { urlParams: { id } } });
        },
        getWithdrawTip() {
            return api.fetch(ADMIN_API.GET_WITHDRAW_TIP).pipe(map((res) => {
                return {
                    ...res,
                    maintain: res.maintain.map(item => convertUnixToDate(item, 'start_time', 'end_time'))
                };
            }));
        },
        updateWithdrawTip(payload) {
            const data = {
                ...payload,
                maintain: payload.maintain.map(item => ({
                    ...item,
                    start_time: getUnixTime(item.start_time),
                    end_time: getUnixTime(item.end_time)
                }))
            };
            return api.fetch(ADMIN_API.POST_WITHDRAW_TIP, { data });
        },
        getWithdrawFees() {
            return api.fetch(ADMIN_API.GET_WITHDRAW_FEES);
        },
        updateWithdrawFees(data) {
            return api.fetch(ADMIN_API.POST_WITHDRAW_FEES, { data });
        },
        getWallets(account, types) {
            return api.fetch(ADMIN_API.GET_WALLETS, { data: { account, types } });
        },
        transferWallet(data) {
            return api.fetch(ADMIN_API.POST_WALLET_TRANSFER, { data });
        },
        dumpWallet(account) {
            return api.fetch(ADMIN_API.POST_WALLET_DUMP, { data: { account } });
        },
        getEffectiveCash(data) {
            return api.fetch(ADMIN_API.GET_EFFECTIVE_CASH, { data });
        },
        updateEffectiveCash(data) {
            return api.fetch(ADMIN_API.POST_EFFECTIVE_CASH, { data });
        },
        getEffectiveCashHistory(payload) {
            const data = {
                ...payload
            };
            if (payload.start_at)
                data.start_at = getUnixTime(payload.start_at);
            if (payload.end_at)
                data.end_at = getUnixTime(payload.end_at);
            return api.fetch(ADMIN_API.GET_EFFECTIVE_CASH_HISTORY, { data }).pipe(map((res) => ({
                ...res,
                data: res.data.map((item) => convertUnixToDate(item, 'created_at', 'updated_at')),
                reset_ats: res?.reset_ats.map((item) => convertUnixToDate(item, 'reset_at')) ?? []
            })), map(res => convertUnixToDate(res, 'last_reset_at', 'previous_reset_at')));
        },
        exportWithdrawSlip(payload) {
            const data = { ...payload };
            if (data.created_at_start)
                data.created_at_start = getUnixTime(data.created_at_start);
            if (data.created_at_end)
                data.created_at_end = getUnixTime(data.created_at_end);
            if (data.finished_at_start)
                data.finished_at_start = getUnixTime(data.finished_at_start);
            if (data.finished_at_end)
                data.finished_at_end = getUnixTime(data.finished_at_end);
            return api.fetch(ADMIN_API.GET_EXPORT_WITHDRAW_SLIP_VALIDATE, { data });
        },
        getBarriersPreview(data) {
            return api.fetch(ADMIN_API.GET_BARRIERS_PREVIEW, { data }).pipe(map((res) => convertUnixToDate(res, 'last_reset_at')));
        },
        getWithdrawBarrierStepByStep(payload) {
            const { account } = payload;
            return api.fetch(ADMIN_API.GET_WITHDRAW_BARRIER_STEP_BY_STEP, { data: { account } }).pipe(map(res => convertUnixToDate(res, 'last_reset_at', 'previous_reset_at')));
        }
    };
}
//# sourceMappingURL=WithdrawService.js.map